import React, { useCallback, useEffect, useState } from 'react';
import { FileText, ArrowRight, ChevronDown } from 'lucide-react';
import { Link } from 'react-router-dom';
import { notesService } from './notesService';
import NotesModal from './NotesModal';
import './NotesRecentCard.css';

const SHOW_COUNT = 4;
const COLLAPSE_KEY = 'revstar_notes_card_collapsed';

function timeAgo(date) {
  const ms = Date.now() - new Date(date).getTime();
  if (ms < 60_000) return 'just now';
  const mins = Math.floor(ms / 60_000);
  if (mins < 60) return `${mins}m`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h`;
  const days = Math.floor(hrs / 24);
  if (days < 7) return `${days}d`;
  return new Date(date).toLocaleDateString();
}

/**
 * Body is stored as HTML (contentEditable). Strip tags and entities for
 * the small card preview so we never leak raw markup into the UI.
 */
function snippet(body, n = 56) {
  if (!body) return '';
  let text = String(body).replace(/<style[\s\S]*?<\/style>/gi, '');
  text = text.replace(/<[^>]+>/g, ' ');             // strip tags
  text = text.replace(/&nbsp;/g, ' ')               // common entities
             .replace(/&amp;/g, '&')
             .replace(/&lt;/g, '<')
             .replace(/&gt;/g, '>')
             .replace(/&quot;/g, '"')
             .replace(/&#39;/g, "'");
  text = text.replace(/\s+/g, ' ').trim();
  return text.length > n ? text.slice(0, n) + '…' : text;
}

/**
 * Overview rail widget showing the merchant's most-recent notes.
 * Click anywhere on the card opens the Notes modal. Refreshes
 * when the modal closes (most likely time the list changed).
 */
const NotesRecentCard = () => {
  const [notes, setNotes] = useState([]);
  const [total, setTotal] = useState(0);
  const [open, setOpen] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [collapsed, setCollapsed] = useState(() => {
    try { return localStorage.getItem(COLLAPSE_KEY) === '1'; } catch { return false; }
  });

  const toggleCollapsed = () => {
    setCollapsed((c) => {
      const next = !c;
      try { localStorage.setItem(COLLAPSE_KEY, next ? '1' : '0'); } catch {}
      return next;
    });
  };

  const refresh = useCallback(async () => {
    try {
      const data = await notesService.list();
      setNotes(data.slice(0, SHOW_COUNT));
      setTotal(data.length);
    } catch (err) {
      // Silent: the Overview shouldn't break if /notes 500s
      console.warn('[NotesRecentCard] load failed', err?.message);
    } finally {
      setLoaded(true);
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);
  useEffect(() => { if (!open) refresh(); }, [open, refresh]);

  return (
    <>
      <section className={`ov-card rsn-card ${collapsed ? 'is-collapsed' : ''}`} aria-label="Notes">
        <header className="rsn-card__head">
          <button
            type="button"
            className="rsn-card__toggle"
            onClick={toggleCollapsed}
            aria-expanded={!collapsed}
            aria-label={collapsed ? 'Expand notes' : 'Collapse notes'}
          >
            <span className="rsn-card__icon" aria-hidden="true">
              <FileText size={16} strokeWidth={2} />
            </span>
            <span className="rsn-card__heads">
              <span className="rsn-card__title">Notes</span>
              <span className="rsn-card__sub">
                {!loaded
                  ? 'Loading…'
                  : notes.length === 0
                    ? 'Quick scratchpad'
                    : `Recent · ${total}`}
              </span>
            </span>
            <span className="rsn-card__chevron" aria-hidden="true">
              <ChevronDown size={16} strokeWidth={2} />
            </span>
          </button>
          <button
            type="button"
            className="rsn-card__open"
            onClick={(e) => { e.stopPropagation(); setOpen(true); }}
            aria-label="Open notes"
          >
            Open
          </button>
        </header>

        <div className="rsn-card__collapse">
          <div className="rsn-card__collapse-inner">
            {loaded && notes.length > 0 ? (
              <ul className="rsn-card__list" role="list">
                {notes.map((n) => (
                  <li key={n.id}>
                    <button
                      type="button"
                      className="rsn-card__entry"
                      onClick={() => setOpen(true)}
                    >
                      <span className="rsn-card__entry-head">
                        <span className="rsn-card__entry-title">{n.title || 'Untitled'}</span>
                        <span className="rsn-card__entry-time">{timeAgo(n.updatedAt)}</span>
                      </span>
                      {n.body && <span className="rsn-card__entry-body">{snippet(n.body)}</span>}
                    </button>
                  </li>
                ))}
              </ul>
            ) : loaded ? (
              <button
                type="button"
                className="rsn-card__empty"
                onClick={() => setOpen(true)}
              >
                Capture a thought — it'll show up here.
              </button>
            ) : null}

            {total > SHOW_COUNT && (
              <Link to="/utilities/notes" className="rsn-card__more">
                See all <ArrowRight size={12} aria-hidden="true" />
              </Link>
            )}
          </div>
        </div>
      </section>

      <NotesModal open={open} onClose={() => setOpen(false)} />
    </>
  );
};

export default NotesRecentCard;
